import React, { useMemo, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';

function parseCsvValues(value) {
  return String(value || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

/**
 * Multi-select dropdown (matches mobile FormMultiSelect).
 */
export default function MultiSelect({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  error = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selectedValues = useMemo(() => parseCsvValues(value), [value]);
  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

  const mergedOptions = useMemo(() => {
    const extras = selectedValues
      .filter((item) => !options.some((option) => option.value === item))
      .map((item) => ({ value: item, label: item }));
    return extras.length ? [...extras, ...options] : options;
  }, [options, selectedValues]);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return mergedOptions;
    return mergedOptions.filter(
      (option) =>
        option.label.toLowerCase().includes(term) ||
        option.value.toLowerCase().includes(term),
    );
  }, [mergedOptions, query]);

  const displayLabel =
    selectedValues.length === 0
      ? placeholder
      : selectedValues.length === 1
        ? mergedOptions.find((o) => o.value === selectedValues[0])?.label ?? selectedValues[0]
        : `${selectedValues.length} selected`;

  const toggle = (optionValue) => {
    const next = new Set(selectedSet);
    if (next.has(optionValue)) {
      next.delete(optionValue);
    } else {
      next.add(optionValue);
    }
    onChange(Array.from(next).join(', '));
  };

  const triggerClass = `themed-input w-full px-4 py-3 rounded-lg text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] ${
    error ? 'border-red-500 focus:ring-red-500' : ''
  }`;

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setQuery('');
          setOpen(true);
        }}
        className={triggerClass}
      >
        <span className={`truncate ${selectedValues.length ? 'text-[var(--color-text)]' : 'text-[var(--color-text-muted)]'}`}>
          {displayLabel}
        </span>
        <ChevronDown className="w-4 h-4 text-[var(--color-text-muted)] shrink-0 ml-2" />
      </button>

      {selectedValues.length > 0 ? (
        <p className="text-xs text-[var(--color-text-muted)] mt-2">Selected: {selectedValues.join(', ')}</p>
      ) : null}

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="elevated-card w-full max-w-md rounded-xl max-h-[70vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-3 border-b border-[var(--color-border)]">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search..."
                autoFocus
                className="themed-input w-full px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              />
            </div>
            <div className="overflow-y-auto flex-1 p-1">
              {filtered.length === 0 ? (
                <p className="px-4 py-3 text-sm text-[var(--color-text-muted)]">No matches found</p>
              ) : (
                filtered.map((option) => {
                  const active = selectedSet.has(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      className={`w-full text-left px-4 py-2.5 text-sm rounded-lg hover:bg-[var(--color-primary-soft)] text-[var(--color-text)] flex items-center gap-2 ${
                        active ? 'bg-[var(--color-primary)]/20' : ''
                      }`}
                      onClick={() => toggle(option.value)}
                    >
                      <span
                        className={`w-4 h-4 shrink-0 rounded border flex items-center justify-center ${
                          active ? 'bg-[var(--color-primary)] border-[var(--color-primary)]' : 'border-[var(--color-border-subtle)]'
                        }`}
                      >
                        {active ? <Check className="w-3 h-3 text-[var(--color-text-inverse)]" /> : null}
                      </span>
                      <span>{option.label}</span>
                    </button>
                  );
                })
              )}
            </div>
            <div className="p-3 border-t border-[var(--color-border)]">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="brand-gradient-btn w-full px-4 py-2 rounded-lg font-medium"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
